import React, { useState } from 'react'
import type { Building, Unit } from '../data/mockSpaceYieldData'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { AlertCircle, ChevronDown, ChevronUp } from 'lucide-react'

interface BuildingYieldBarsProps {
  buildings: Building[]
  includeFuel: boolean
}

// Internal component for the row to maintain expanded state cleanly
const BuildingYieldRow: React.FC<{
    building: Building, 
    includeFuel: boolean, 
    marketAvg: number,
    MAX_REV: number,
    MAX_YIELD: number,
    getSegmentWidth: (units: Unit[], building: Building, type: 'lease' | 'transient' | 'idle' | 'fuel') => number,
    formatCurrency: (val: number) => string
}> = ({ building, includeFuel, marketAvg, MAX_REV, MAX_YIELD, getSegmentWidth, formatCurrency }) => {
    const [expanded, setExpanded] = useState(false)

    const leaseWidth = getSegmentWidth(building.units, building, 'lease')
    const transientWidth = getSegmentWidth(building.units, building, 'transient')
    const fuelWidth = getSegmentWidth(building.units, building, 'fuel')
    const idleWidth = getSegmentWidth(building.units, building, 'idle')
    
    const renderedRev = includeFuel ? building.total_value : building.revenue;
    const renderedYield = includeFuel ? building.avg_total_yield : building.avg_yield;
    const isPricingProblem = building.occupancy_pct >= 80 && renderedYield < marketAvg;

    const renderSegments = () => (
       <>
          {leaseWidth > 0 && <div className="bg-emerald-500 h-full transition-colors" style={{ width: `${leaseWidth}%` }} />}
          {transientWidth > 0 && <div className="bg-blue-500 h-full transition-colors" style={{ width: `${transientWidth}%` }} />}
          {includeFuel && fuelWidth > 0 && <div className="bg-amber-400 h-full transition-colors" style={{ width: `${fuelWidth}%` }} />}
          {!includeFuel && idleWidth > 0 && <div className="bg-slate-200 h-full transition-colors" style={{ width: `${idleWidth}%` }} />}
       </>
    );

    // Calculate sub-yields
    const leaseUnits = building.units.filter(u => u.revenue_segment === 'lease' || u.revenue_segment === 'hybrid');
    const transientUnits = building.units.filter(u => u.revenue_segment === 'transient');

    const computeYield = (units: Unit[]) => {
        const sqft = units.reduce((s, u) => s + u.sqft, 0);
        const spaceRev = units.reduce((s, u) => s + (u.lease_rate || 0) + (u.transient_revenue || 0), 0);
        const fuelRev = units.reduce((s, u) => s + (u.fuel_revenue || 0), 0);
        const spaceYld = sqft > 0 ? spaceRev / sqft : 0;
        const totalYld = sqft > 0 ? (spaceRev + fuelRev) / sqft : 0;
        return { sqft, spaceRev, fuelRev, spaceYld, totalYld, renderedYld: includeFuel ? totalYld : spaceYld };
    };

    const lYields = computeYield(leaseUnits);
    const tYields = computeYield(transientUnits);

    const renderYieldBar = (spaceYld: number, totalYld: number, max: number, includeF: boolean) => {
        const primaryYield = spaceYld;
        const secYield = totalYld - spaceYld;
        
        return (
            <div className="h-2 md:h-2.5 bg-slate-100 rounded-sm w-32 relative flex shrink-0">
                <div 
                    className={`h-full ${!includeF || secYield <= 0 ? 'rounded-sm' : 'rounded-l-sm'} opacity-90 ${primaryYield < marketAvg ? 'bg-rose-400' : 'bg-emerald-400'}`} 
                    style={{ width: `${Math.min(100, (primaryYield / max) * 100)}%` }}
                    title={`Space Yield: $${primaryYield.toFixed(2)}`}
                />
                {includeF && secYield > 0 && (
                    <div 
                        className="h-full bg-amber-400 opacity-90 rounded-r-sm border-l border-white/30" 
                        style={{ width: `${Math.min(100, (secYield / max) * 100)}%` }}
                        title={`Fuel Yield: $${secYield.toFixed(2)}`}
                    />
                )}
                {/* Target Line */}
                <TooltipProvider delayDuration={0}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div 
                                className="absolute top-[-2px] bottom-[-2px] w-[2px] bg-slate-400 border-l border-white shadow-sm cursor-help hover:bg-slate-600 transition-colors"
                                style={{ left: `${(marketAvg / max) * 100}%` }}
                            />
                        </TooltipTrigger>
                        <TooltipContent>Market Avg: ${marketAvg.toFixed(2)}/sqft</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
        )
    }

    return (
        <div className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors -mx-6 px-6">
            {/* The Main Row */}
            <div 
                className="py-4 flex items-center group relative cursor-pointer"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="w-8 shrink-0 flex items-center justify-center text-slate-400 group-hover:text-slate-600">
                    {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>

                {/* 1. Label Column */}
                <div className="w-[140px] md:w-[180px] shrink-0 pr-4 pl-2">
                    <div className="font-semibold text-slate-900 truncate" title={building.name}>{building.name}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{building.total_units} units &middot; {building.total_sqft.toLocaleString()} sf</div>
                </div>
                
                {/* 3 Metric Columns */}
                <div className="flex-1 grid grid-cols-3 gap-6 md:gap-8 items-center">
                    
                    {/* Metric 1: Revenue Column */}
                    <div className="flex items-center gap-3">
                        <TooltipProvider delayDuration={100}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className="h-2 md:h-2.5 bg-slate-100 rounded-sm flex-1 flex overflow-hidden">
                                        <div className="h-full flex w-full" style={{ width: `${Math.min(100, (renderedRev / MAX_REV) * 100)}%` }}>
                                           {renderSegments()}
                                        </div>
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent className="text-xs">
                                    <p className="font-semibold">{building.name} {includeFuel ? 'Value' : 'Revenue'}</p>
                                    <p>🟢 Lease: {includeFuel ? `${leaseWidth.toFixed(0)}% rev` : `${leaseWidth.toFixed(0)}% sqft`}</p>
                                    <p>🔵 Transient: {includeFuel ? `${transientWidth.toFixed(0)}% rev` : `${transientWidth.toFixed(0)}% sqft`}</p>
                                    {includeFuel && <p>🟠 Fuel: {fuelWidth.toFixed(0)}% rev</p>}
                                    {!includeFuel && <p>⚪ Idle: {idleWidth.toFixed(0)}% sqft</p>}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                        <div className="w-12 md:w-16 text-right font-mono text-xs md:text-sm font-semibold text-slate-700">
                            {formatCurrency(renderedRev)}
                        </div>
                    </div>

                    {/* Metric 2: Occupancy Column */}
                    <div className="flex items-center gap-3 opacity-90 group-hover:opacity-100 transition-opacity">
                        <div className="h-2 md:h-2.5 bg-slate-100 rounded-sm flex-1 flex overflow-hidden">
                           {renderSegments()}
                        </div>
                        <div className="w-8 text-right font-mono text-xs md:text-sm font-medium text-slate-600">
                            {building.occupancy_pct}%
                        </div>
                    </div>

                    {/* Metric 3: Yield Column */}
                    <div className="flex items-center gap-3 relative">
                        {renderYieldBar(building.avg_yield, building.avg_total_yield, MAX_YIELD, includeFuel)}
                        
                        <div className="w-12 text-right font-mono text-xs md:text-sm font-bold text-slate-800 flex items-center justify-end gap-1.5 whitespace-nowrap">
                            ${renderedYield.toFixed(2)}
                            <span className="text-[10px] text-slate-400 font-normal ml-1">avg</span>
                        </div>

                        {/* Warning Indicator */}
                        {isPricingProblem && !includeFuel && (
                            <TooltipProvider delayDuration={0}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className="absolute -right-6 flex items-center justify-center cursor-help hidden md:flex">
                                            <AlertCircle className="w-4 h-4 text-amber-500 animate-pulse" />
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-amber-50 border-amber-200 text-amber-900 shadow-md">
                                        <p className="font-bold">Pricing Alert</p>
                                        <p className="text-xs">High occupancy ({building.occupancy_pct}%) but below-market yield.</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    </div>
                </div>
            </div>

            {/* Drill-Down Expandable Area */}
            {expanded && (
                <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-4 ml-8 mr-2 space-y-3 relative shadow-sm">
                    {/* Decorative connector */}
                    <div className="absolute -left-6 top-0 bottom-4 w-px bg-slate-200" />
                    <div className="absolute -left-6 top-4 w-6 h-px bg-slate-200" />
                    <div className="absolute -left-6 top-12 w-6 h-px bg-slate-200" />

                    {/* Lease Breakdown Row */}
                    {lYields.sqft > 0 && (
                        <div className="flex items-center text-xs">
                            <div className="w-[140px] text-slate-600 font-medium flex items-center gap-2">
                                <div className="w-2 h-2 rounded-sm bg-emerald-500" /> Lease Yield
                            </div>
                            <div className="flex-1" />
                            <div className="flex items-center gap-3">
                                {renderYieldBar(lYields.spaceYld, lYields.totalYld, MAX_YIELD, includeFuel)}
                                <div className="w-12 text-right font-mono font-bold text-slate-700">
                                    ${lYields.renderedYld.toFixed(2)}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Transient Breakdown Row */}
                    {tYields.sqft > 0 && (
                        <div className="flex items-center text-xs">
                            <div className="w-[140px] text-slate-600 font-medium flex items-center gap-2">
                                <div className="w-2 h-2 rounded-sm bg-blue-500" /> Transient Yield
                            </div>
                            <div className="flex-1" />
                            <div className="flex items-center gap-3">
                                {renderYieldBar(tYields.spaceYld, tYields.totalYld, MAX_YIELD, includeFuel)}
                                <div className="w-12 text-right font-mono font-bold text-slate-700">
                                    ${tYields.renderedYld.toFixed(2)}
                                </div>
                            </div>
                        </div>
                    )}

                    {lYields.sqft === 0 && tYields.sqft === 0 && (
                        <div className="text-xs text-slate-400 italic py-1">No active units to segment.</div>
                    )}
                </div>
            )}
        </div>
    )
}

export const BuildingYieldBars: React.FC<BuildingYieldBarsProps> = ({ buildings, includeFuel }) => {
  
  const formatCurrency = (val: number) => 
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val)
    
  const getSegmentWidth = (units: Unit[], building: Building, type: 'lease' | 'transient' | 'idle' | 'fuel') => {
    if (includeFuel) {
        // Value proportion approach
        if (building.total_value === 0) return 0
        if (type === 'lease') return (units.reduce((s,u) => s + (u.lease_rate || 0), 0) / building.total_value) * 100
        if (type === 'transient') return (units.reduce((s,u) => s + (u.transient_revenue || 0), 0) / building.total_value) * 100
        if (type === 'fuel') return (units.reduce((s,u) => s + (u.fuel_revenue || 0), 0) / building.total_value) * 100
        return 0 // no idle in value proportion
    } else {
        // purely sqft
        if (type === 'fuel') return 0
        const totalSqft = units.reduce((sum, u) => sum + u.sqft, 0)
        if (totalSqft === 0) return 0
        
        let segmentSqft = 0
        if (type === 'lease') {
           segmentSqft = units.filter(u => u.revenue_segment === 'lease' || u.revenue_segment === 'hybrid').reduce((sum, u) => sum + u.sqft, 0)
        } else if (type === 'transient') {
           segmentSqft = units.filter(u => u.revenue_segment === 'transient').reduce((sum, u) => sum + u.sqft, 0)
        } else {
           segmentSqft = units.filter(u => u.revenue_segment === 'idle').reduce((sum, u) => sum + u.sqft, 0)
        }
        
        return (segmentSqft / totalSqft) * 100
    }
  }

  // Top-level max values for scalable charts (using constants that were in the variation page)
  const MAX_REV = 40000;
  const MAX_YIELD = 1.5;
  const MARKET_AVG = 1.05;

  return (
    <div className="space-y-4 bg-white p-6 rounded-lg border shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-900">Building Performance &amp; Pricing Alerts</h3>
        <p className="text-xs text-slate-500">Click a row to expand detailed yield analysis.</p>
      </div>
      
      <div className="border-t border-slate-100 -mx-6">
        {/* Header Row */}
        <div className="flex items-center py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-6 border-b border-slate-100 bg-slate-50/50">
           <div className="w-8 shrink-0" /> {/* Chevron placeholder */}
           <div className="w-[140px] md:w-[180px] shrink-0 pl-2">Facility</div>
           <div className="flex-1 grid grid-cols-3 gap-6 md:gap-8">
               <div>1. {includeFuel ? 'Total Value' : 'Space Revenue'}</div>
               <div>2. Occupancy</div>
               <div>3. Yield / Sqft</div>
           </div>
        </div>

        {buildings.map(building => (
           <BuildingYieldRow 
               key={building.id}
               building={building}
               includeFuel={includeFuel}
               marketAvg={MARKET_AVG}
               MAX_REV={MAX_REV}
               MAX_YIELD={MAX_YIELD}
               getSegmentWidth={getSegmentWidth}
               formatCurrency={formatCurrency}
           />
        ))}
      </div>
    </div>
  )
}
